import React from 'react';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { focusByOrder } from 'platform/utilities/ui/focus';
import { scrollToTop } from 'platform/utilities/scroll';
import { isCompletingForm0781 } from '../utils';
import eventDetails from './traumaticEventDetails';
import { officialReportCustom } from './officialReport';
import policeReport from './policeReportLocation';
import OfficialReport from '../components/OfficialReport';
import {
  eventsListPageTitle,
  eventsListDescription,
  maxEventsAlert,
} from '../content/traumaticEventsList';
import { eventDetailsPageTitle } from '../content/traumaticEventDetails';
import { officialReportPageTitle } from '../content/officialReport';
import { policeReportLocationPageTitle } from '../content/policeReportLocation';
import { form0781HeadingTag, mentalHealthSupportAlert } from '../content';
import { MILITARY_REPORT_TYPES, OTHER_REPORT_TYPES } from '../constants';

const scrollAndFocusToProgressBar = () => {
  scrollToTop('topScrollElement');
  focusByOrder(['form h3', 'va-segmented-progress-bar']);
};
const isReviewAndSubmitPage = () => {
  const pathParts = window.location.pathname.split('/');
  return pathParts[pathParts.length - 1] === 'review-and-submit';
};
const summaryTitleUI = (summaryTitle, reviewAndSubmitTitle) => {
  return () => (isReviewAndSubmitPage() ? reviewAndSubmitTitle : summaryTitle);
};
const summaryDescriptionUI = (
  description,
  reviewAndSubmitDescription = null,
) => {
  return () =>
    isReviewAndSubmitPage() ? reviewAndSubmitDescription : description();
};

export const summaryPageTitleWithTag = (
  <>
    <span className="vads-u-display--block vads-u-font-family--sans vads-u-font-size--base vads-u-font-weight--normal">
      {form0781HeadingTag}
    </span>
    <span className="vads-u-display--block vads-u-font-size--h3 vads-u-color--base">
      {eventsListPageTitle}
    </span>
  </>
);

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'events',
  nounSingular: 'event',
  nounPlural: 'events',
  useButtonInsteadOfYesNo: true,
  required: false,
  maxItems: 20,
  text: {
    summaryTitle: summaryTitleUI(summaryPageTitleWithTag, eventsListPageTitle),
    summaryTitleWithoutItems: summaryPageTitleWithTag,
    summaryDescription: summaryDescriptionUI(eventsListDescription, null),
    summaryDescriptionWithoutItems: eventsListDescription,
    getItemName: (props, index) => {
      return `Event #${index + 1}`;
    },
    cardDescription: item => {
      const selectedReports = [
        ...Object.entries(item?.militaryReports || {})
          .filter(([_, isSelected]) => isSelected)
          .map(([key]) => MILITARY_REPORT_TYPES[key]),
        ...Object.entries(item?.otherReports || {})
          .filter(([_, isSelected]) => isSelected)
          .map(([key]) => OTHER_REPORT_TYPES[key]),
      ];
      if (item?.unlistedReport) {
        selectedReports.push(item.unlistedReport);
      }
      const reportsDescription = selectedReports.join('; ');
      return (
        <div className="form0781-summary-card">
          <p className="form0781-summary-card-response">
            <strong>Description:</strong> {item?.details}
          </p>
          <p className="form0781-summary-card-response">
            <strong>Location:</strong> {item?.location}
          </p>
          <p className="form0781-summary-card-response">
            <strong>Date:</strong> {item?.timing}
          </p>
          <p className="form0781-summary-card-response">
            <strong>Official Report:</strong> {reportsDescription}
          </p>
        </div>
      );
    },
    summaryAddButtonText: 'Add an event',
    reviewAddButtonText: 'Add an event',
    alertMaxItems: maxEventsAlert,
    alertItemUpdated: props => {
      return `You’ve edited details about Event #${props.index + 1}`;
    },
    alertItemDeleted: props => {
      return `You’ve deleted details about Event #${props.index + 1}`;
    },
    cancelAddTitle: 'Are you sure you want to cancel adding this event?',
    cancelAddYes: 'Yes, cancel adding',
    cancelAddNo: 'No, continue adding this event',
    cancelEditButtonText: 'Cancel editing this event',
    cancelEditDescription:
      'If you cancel, you’ll lose any changes you made on this screen.',
    cancelEditNo: 'No, continue editing',
    cancelEditTitle: 'Are you sure you want to cancel editing this event?',
    deleteTitle: 'Are you sure you want to delete this event?',
    deleteDescription:
      'If you delete this event, you’ll lose the information you entered about this event.',
    deleteYes: 'Yes, delete',
    deleteNo: 'No, keep this event',
  },
};

export const traumaticEventsPages = arrayBuilderPages(options, pageBuilder => ({
  eventsList: pageBuilder.summaryPage({
    title: summaryPageTitleWithTag,
    path: 'mental-health-form-0781/events-summary',
    scrollAndFocusTarget: scrollAndFocusToProgressBar,
    depends: formData => isCompletingForm0781(formData),
    ContentBeforeButtons: (
      <div className="vads-u-margin-y--2p5">{mentalHealthSupportAlert()}</div>
    ),
  }),
  eventDetails: pageBuilder.itemPage({
    title: eventDetailsPageTitle,
    path: 'mental-health-form-0781/:index/event-details',
    scrollAndFocusTarget: scrollAndFocusToProgressBar,
    depends: formData => isCompletingForm0781(formData),
    uiSchema: eventDetails.uiSchema,
    schema: eventDetails.schema,
  }),
  officialReport: pageBuilder.itemPage({
    title: officialReportPageTitle,
    path: `mental-health-form-0781/:index/event-report`,
    scrollAndFocusTarget: scrollAndFocusToProgressBar,
    depends: (formData, index) => formData.events?.[index],
    CustomPage: OfficialReport,
    uiSchema: officialReportCustom.uiSchema,
    schema: officialReportCustom.schema,
  }),
  policeReport: pageBuilder.itemPage({
    title: policeReportLocationPageTitle,
    path: `mental-health-form-0781/:index/event-police-report`,
    scrollAndFocusTarget: scrollAndFocusToProgressBar,
    depends: (formData, index) =>
      isCompletingForm0781(formData) &&
      formData.events?.[index]?.otherReports?.police,
    uiSchema: policeReport.uiSchema,
    schema: policeReport.schema,
  }),
}));
